import format from 'date-fns/format';
import intervalToDuration from 'date-fns/intervalToDuration';
import isFuture from 'date-fns/isFuture';
import isToday from 'date-fns/isToday';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import Button from 'components/Button';
import { ErrorMsg, SuccessMsg } from 'components/ContactForm/FormFeedback';
import Input from 'components/Form/Input';
import SelectInput from 'components/Form/SelectInput';
import Link from 'components/Link';
import { useClaysLocation } from 'context/ClaysLocationProvider';

import { Grid } from 'styles/grid';

import { WEBSITE_URL } from 'utils/constants';
import validation from 'utils/form-validation-schema';
import { formatSlug, formatSlugToString } from 'utils/helpers';

import GoogleReCaptcha from './GoogleReCaptcha';

import {
  CheckboxStyled,
  Container,
  Form,
  InputDateField,
  Paragraph,
  Subtitle,
  Title,
  TitleSection,
} from 'components/ContactForm/styles';

const isEighteenOrOver = (dob) => {
  const age = intervalToDuration({ start: new Date(), end: new Date(dob) });
  return age.years >= 18;
};

const isValidStartTime = (startTime, open = 12, close = 23) => {
  const time = parseInt(startTime.substring(0, 2));
  return time >= open && time <= close;
};

const GTM_IDENTIFIER = 'gtm-group-bookings';

const formFieldIds = {
  firstName: 'first_name',
  lastName: 'last_name',
  jobTitle: 'title',
  company: 'company',
  email: 'email',
  phone: 'phone',
  numberOfGuests: '00N8d00000FQkuR',
  dateOfBirth: '00N8d00000FRQPV',
  budgetPerPerson: '00N8d00000FQkue',
  eventPackage: '00N8d00000FRQQN',
  reservationDate: '00N8d00000FRQPL',
  marketingOptIn: '00N8d00000FRQR3',
  dateFlexibility: '00N8d00000FRQQa',
  occasion: '00N8d00000FRQOs',
  preferredLocation: '00N8d00000FcjgG',
  bookingStartTime: '00N8d00000FRQPQ',
  acceptTerms: 'acceptTerms',
};

const GroupBookingsForm = ({
  page: { title, description, formFieldData, claysLocation },
}) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm();

  const router = useRouter();

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [isReCaptchaSuccessful, setIsReCaptchaSuccessful] = useState(false);

  const { openingHours } = formFieldData || {};
  const { allClaysLocations } = useClaysLocation();

  const preferredLocations =
    allClaysLocations?.map((locations) => formatSlugToString(locations.slug)) ||
    [];

  const {
    firstName,
    lastName,
    company,
    jobTitle,
    email,
    phone,
    dateOfBirth,
    numberOfGuests,
    budgetPerPerson,
    occasion,
    preferredLocation,
    reservationDate,
    bookingStartTime,
    eventPackage,
    dateFlexibility,
    marketingOptIn,
    acceptTerms,
  } = formFieldIds;

  const handleOnSubmit = async (formData) => {
    if (!hasAcceptedTerms || !isReCaptchaSuccessful) return;

    setSubmitting(true);

    const dob = new Date(formData[dateOfBirth]);
    const bookingDate = new Date(formData[reservationDate]);

    const formatData = {
      ...formData,
      [bookingStartTime]: `${formData[bookingStartTime]}:00.000Z`,
      [dateOfBirth]: format(dob, 'dd/MM/yyyy'),
      [reservationDate]: format(bookingDate, 'dd/MM/yyyy'),
      oid: process.env.NEXT_PUBLIC_WEB_TO_LEAD_FORM_OID,
      lead_source: process.env.NEXT_PUBLIC_WEB_TO_LEAD_FORM_SOURCE,
      retURL: `${WEBSITE_URL}/${formatSlug(claysLocation)}`,
    };

    try {
      const res = await fetch('/api/enquiry/web-to-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formatData),
      });

      if (res.ok) {
        setSuccess(true);

        router.push(`${router.asPath}?success=true`, undefined, {
          shallow: true,
        });
      } else {
        setError(true);
      }
    } catch (e) {
      console.error(e);
      setError(true);
    }

    setSubmitting(false);
  };

  if (error) {
    return <ErrorMsg />;
  }

  if (success) {
    return (
      <SuccessMsg
        title="Enquiry sent"
        description="Thank you for contacting Clays. We've received your enquiry and will be in touch shortly!"
        className={`${GTM_IDENTIFIER}-submitted`}
      />
    );
  }

  const hasAcceptedTerms = watch(acceptTerms);
  const todaysDate = format(new Date(), 'yyyy-MM-dd');
  const openTime = openingHours?.open || 12;
  const closeTime = openingHours?.close || 23;
  const minGuests = process.env.NEXT_PUBLIC_GROUP_MIN_GUESTS || 10;
  const minBudget = process.env.NEXT_PUBLIC_GROUP_MIN_BUDGET || 30;

  return (
    <Container theme="light">
      <Grid>
        <TitleSection>
          <Title html={title?.html} />
          <Paragraph indent>{description}</Paragraph>
        </TitleSection>

        <Form onSubmit={handleSubmit(handleOnSubmit)} noValidate>
          <Subtitle>Start a conversation</Subtitle>

          <Input
            label="First name"
            errorMessage={errors[firstName]?.message}
            value={watch(firstName)}
            maxLength={40}
            {...register(firstName, validation.firstName)}
          />

          <Input
            label="Last name"
            errorMessage={errors[lastName]?.message}
            value={watch(lastName)}
            maxLength={80}
            {...register(lastName, validation.lastName)}
          />

          <Input
            label="Job title"
            errorMessage={errors[jobTitle]?.message}
            value={watch(jobTitle)}
            maxLength={40}
            {...register(jobTitle)}
          />

          <Input
            label="Company name"
            errorMessage={errors[company]?.message}
            value={watch(company)}
            maxLength={40}
            {...register(company)}
          />

          <Input
            label="Email address"
            type="email"
            errorMessage={errors[email]?.message}
            value={watch(email)}
            maxLength={80}
            {...register(email, validation.email)}
          />

          <Input
            label="Phone number"
            type="number"
            errorMessage={errors[phone]?.message}
            value={watch(phone)}
            maxLength={40}
            {...register(phone, {
              required: 'Please enter your phone number',
            })}
          />

          <InputDateField
            label="Date of birth"
            placeholder="I acknowledge that I am over 18"
            max={todaysDate}
            onFocus={(e) => (e.target.type = 'date')}
            errorMessage={errors[dateOfBirth]?.message}
            setValue={setValue}
            {...register(dateOfBirth, {
              required: 'Please enter your date of birth',
              validate: (value) =>
                isEighteenOrOver(value) || 'Our venue is over 18s only',
            })}
          />

          <Input
            label="Number of guests"
            type="number"
            placeholder="Enter the number of guests"
            errorMessage={errors[numberOfGuests]?.message}
            setValue={setValue}
            {...register(numberOfGuests, {
              required: 'Please enter the number of guests',
              validate: (value) =>
                value >= minGuests || `Minimum of ${minGuests} guests`,
            })}
          />

          <Input
            label="Budget per person (including VAT)"
            type="number"
            placeholder="Enter a budget per person"
            errorMessage={errors[budgetPerPerson]?.message}
            setValue={setValue}
            {...register(budgetPerPerson, {
              required: 'Please select a budget per person',
              validate: (value) =>
                value >= minBudget || `Value must be greater than ${minBudget}`,
            })}
          />

          {formFieldData && (
            <SelectInput
              label="Occasion"
              placeholder="Select an occasion"
              options={formFieldData.occasion || []}
              errorMessage={errors[occasion]?.message}
              setValue={setValue}
              {...register(occasion, {
                required: 'Please select an occasion',
              })}
            />
          )}

          <SelectInput
            label="Preferred Location"
            placeholder="Select a preferred location"
            options={preferredLocations || []}
            errorMessage={errors[preferredLocation]?.message}
            setValue={setValue}
            {...register(preferredLocation, {
              required: 'Please select a preferred location',
            })}
          />

          {formFieldData && (
            <SelectInput
              label="Preferred package"
              placeholder="Select a package"
              options={formFieldData.eventPackage || []}
              errorMessage={errors[eventPackage]?.message}
              setValue={setValue}
              {...register(eventPackage, {
                required: 'Please select a package',
              })}
            />
          )}

          <InputDateField
            label="Reservation Date"
            placeholder="Select a date"
            min={todaysDate}
            onFocus={(e) => (e.target.type = 'date')}
            errorMessage={errors[reservationDate]?.message}
            setValue={setValue}
            {...register(reservationDate, {
              required: 'Please select a date',
              validate: (value) => {
                const inputDate = new Date(value);
                if (isToday(inputDate)) return;

                if (!isFuture(inputDate)) {
                  return 'Please select a date in the future';
                }
              },
            })}
          />

          <Input
            label="Booking start time (24hrs)"
            type="time"
            errorMessage={errors[bookingStartTime]?.message}
            setValue={setValue}
            {...register(bookingStartTime, {
              required: 'Please select a start time',
              validate: (value) =>
                isValidStartTime(value, openTime, closeTime) ||
                `Please select a time between ${openTime}:00 and ${closeTime}:00`,
            })}
          />

          <CheckboxStyled {...register(dateFlexibility)}>
            Date Flexibility
          </CheckboxStyled>

          <CheckboxStyled {...register(marketingOptIn)}>
            I am happy for Clays to send me updates on latest happenings and
            special offers.
          </CheckboxStyled>

          <CheckboxStyled {...register(acceptTerms)}>
            I have read and accept the terms of processing data in the{' '}
            <Link href="/privacy-policy" targetBlank>
              Privacy Policy.
            </Link>
          </CheckboxStyled>

          <GoogleReCaptcha setIsSuccessful={setIsReCaptchaSuccessful} />

          <Button
            disabled={!hasAcceptedTerms || submitting || !isReCaptchaSuccessful}
            type="submit"
            className={GTM_IDENTIFIER}
          >
            Send message
          </Button>
        </Form>
      </Grid>
    </Container>
  );
};

export default GroupBookingsForm;
